import type { AcademicTerm, AcademicYear } from "../../services/academicSetupService";
import type { Curriculum, Subject, Syllabus } from "../../services/curriculumService";
import type { Exam } from "../../services/examinationService";
import { byNumber, byText, humanise, reversed, type RegisterConfig } from "../register/registerLogic";

/**
 * The Academics registers -- calendar, subjects, curricula, syllabi and
 * examinations -- and the wording and counts they share.
 */

export const labelFrom = (pairs: [string, string][], value: string | number | null | undefined) =>
  pairs.find(([key]) => key === value)?.[1] ?? humanise(value);

export const SUBJECT_CATEGORIES: [string, string][] = [
  ["core", "Core"],
  ["elective", "Elective"],
  ["life_skills", "Life skills"],
  ["vocational", "Vocational"],
  ["extracurricular", "Extracurricular"],
];
export const CURRICULUM_TYPES: [string, string][] = [
  ["national", "National"],
  ["international", "International"],
  ["custom", "School's own"],
  ["hybrid", "Hybrid"],
];
export const EDUCATION_LEVELS: [string, string][] = [
  ["pre_primary", "Pre-primary"],
  ["primary", "Primary"],
  ["secondary", "Secondary"],
  ["tertiary", "Tertiary"],
];
export const TERM_LABELS: [AcademicTerm["term"], string][] = [
  ["term_1", "Term 1"],
  ["term_2", "Term 2"],
  ["term_3", "Term 3"],
];
export const EXAM_STATUSES: [string, string][] = [
  ["draft", "Draft"],
  ["scheduled", "Scheduled"],
  ["in_progress", "In progress"],
  ["completed", "Completed"],
  ["cancelled", "Cancelled"],
  ["postponed", "Postponed"],
];
export const RESULTS_STATUSES: [string, string][] = [
  ["draft", "Marks being entered"],
  ["submitted", "Awaiting moderation"],
  ["moderated", "Moderated"],
  ["approved", "Approved"],
  ["published", "Published"],
];

export const activeStatus = (record: { is_active: boolean }) =>
  record.is_active ? { label: "Active", dot: "bg-emerald-500" } : { label: "Inactive", dot: "bg-gray-400" };

/** Weeks from one date to another, counting both days. */
export const weeksBetween = (start: string, end: string) =>
  Math.max(0, (Date.parse(end) - Date.parse(start)) / 86_400_000 + 1) / 7;
export const weeksLabel = (start: string, end: string) => {
  const weeks = Math.round(weeksBetween(start, end));
  return `${weeks} ${weeks === 1 ? "week" : "weeks"}`;
};

// --- Calendar --------------------------------------------------------------------

export type YearRow = AcademicYear & { termCount: number };

export const withTermCounts = (years: AcademicYear[] | undefined, terms: AcademicTerm[] | undefined): YearRow[] | undefined =>
  years?.map((year) => ({ ...year, termCount: (terms ?? []).filter((term) => term.academic_year === year.id).length }));

export const termLabel = (term: Pick<AcademicTerm, "term">) => labelFrom(TERM_LABELS, term.term);

/** Where a term stands against today: marked current, running, ahead or over. */
export function termPhase(term: Pick<AcademicTerm, "start_date" | "end_date" | "is_current">, today: string) {
  if (term.is_current) return { label: "Current", dot: "bg-emerald-500" };
  if (today < term.start_date) return { label: "Upcoming", dot: "bg-sky-500" };
  if (today > term.end_date) return { label: "Ended", dot: "bg-gray-400" };
  return { label: "In dates, not marked current", dot: "bg-amber-500" };
}

export const yearConfig: RegisterConfig<YearRow> = {
  search: (row) => row.name,
  views: [
    { key: "all", label: "All" },
    { key: "current", label: "Current", match: (row) => row.is_current },
    { key: "incomplete", label: "Missing terms", match: (row) => row.termCount < 3 },
  ],
  date: { label: "Starts", value: (row) => row.start_date },
  sorts: [
    { key: "-start", label: "Newest first", compare: reversed(byText<YearRow>((row) => row.start_date)) },
    { key: "start", label: "Oldest first", compare: byText<YearRow>((row) => row.start_date) },
  ],
};

export const termConfig = (today: string): RegisterConfig<AcademicTerm> => ({
  search: (row) => `${termLabel(row)} ${row.academic_year_name}`,
  views: [
    { key: "all", label: "All" },
    { key: "current", label: "Current", match: (row) => row.is_current },
    { key: "upcoming", label: "Upcoming", match: (row) => today < row.start_date },
    { key: "ended", label: "Ended", match: (row) => today > row.end_date && !row.is_current },
  ],
  date: { label: "Starts", value: (row) => row.start_date },
  category: { label: "Academic year", value: (row) => row.academic_year_name },
  sorts: [
    { key: "-start", label: "Newest first", compare: reversed(byText<AcademicTerm>((row) => row.start_date)) },
    { key: "start", label: "Oldest first", compare: byText<AcademicTerm>((row) => row.start_date) },
  ],
});

/**
 * The setup order the school follows, each step ticked from the records:
 * a current year, its three terms, and the term in progress marked current.
 */
export function calendarChecklist(years: AcademicYear[], terms: AcademicTerm[]) {
  const current = years.find((year) => year.is_current);
  const currentTerms = current ? terms.filter((term) => term.academic_year === current.id) : [];
  return [
    { label: "Create the academic year", done: years.length > 0 },
    { label: "Mark it as the current year", done: Boolean(current) },
    { label: "Add Term 1, Term 2 and Term 3 to it", done: new Set(currentTerms.map((term) => term.term)).size === 3 },
    { label: "Mark the term in progress as current", done: currentTerms.some((term) => term.is_current) },
  ];
}

// --- Subjects ----------------------------------------------------------------------

export type SubjectRow = Subject & { syllabusCount: number };

export const withSyllabusCounts = (subjects: Subject[] | undefined, syllabi: Syllabus[] | undefined): SubjectRow[] | undefined =>
  subjects?.map((subject) => ({ ...subject, syllabusCount: (syllabi ?? []).filter((syllabus) => syllabus.subject === subject.id).length }));

export const categoryLabel = (category: string) => labelFrom(SUBJECT_CATEGORIES, category);
export const percent = (value: string | number | null | undefined) => `${Number(value ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;

export const subjectConfig: RegisterConfig<SubjectRow> = {
  search: (row) => [row.name, row.code, row.curriculum_name, categoryLabel(row.category), row.description].join(" "),
  views: [
    { key: "all", label: "All" },
    { key: "active", label: "Active", match: (row) => row.is_active },
    { key: "compulsory", label: "Compulsory", match: (row) => row.is_active && row.is_compulsory },
    { key: "optional", label: "Optional", match: (row) => row.is_active && !row.is_compulsory },
    { key: "no-syllabus", label: "No syllabus", match: (row) => row.is_active && row.syllabusCount === 0 },
    { key: "inactive", label: "Inactive", match: (row) => !row.is_active },
  ],
  category: { label: "Category", value: (row) => categoryLabel(row.category) },
  sorts: [
    { key: "name", label: "Name (A–Z)", compare: byText<SubjectRow>((row) => row.name) },
    { key: "code", label: "Code (A–Z)", compare: byText<SubjectRow>((row) => row.code) },
    { key: "-periods", label: "Most periods a week", compare: reversed(byNumber<SubjectRow>((row) => row.weekly_periods)) },
    { key: "curriculum", label: "Curriculum", compare: byText<SubjectRow>((row) => `${row.curriculum_name} ${row.name}`) },
  ],
};

// --- Curricula ----------------------------------------------------------------------

export type CurriculumRow = Curriculum & { subjectCount: number; syllabusCount: number };

export function withCurriculumCounts(
  curricula: Curriculum[] | undefined,
  subjects: Subject[] | undefined,
  syllabi: Syllabus[] | undefined,
): CurriculumRow[] | undefined {
  return curricula?.map((curriculum) => {
    const own = new Set((subjects ?? []).filter((subject) => subject.curriculum === curriculum.id).map((subject) => subject.id));
    return {
      ...curriculum,
      subjectCount: own.size,
      syllabusCount: (syllabi ?? []).filter((syllabus) => own.has(syllabus.subject)).length,
    };
  });
}

export const levelLabel = (level: string) => labelFrom(EDUCATION_LEVELS, level);

export const curriculumConfig: RegisterConfig<CurriculumRow> = {
  search: (row) => [row.name, row.version, row.country, row.description, labelFrom(CURRICULUM_TYPES, row.curriculum_type)].join(" "),
  views: [
    { key: "all", label: "All" },
    { key: "active", label: "Active", match: (row) => row.is_active },
    { key: "inactive", label: "Inactive", match: (row) => !row.is_active },
  ],
  date: { label: "Effective", value: (row) => row.start_date },
  category: { label: "Level", value: (row) => levelLabel(row.education_level) },
  sorts: [
    { key: "name", label: "Name (A–Z)", compare: byText<CurriculumRow>((row) => row.name) },
    { key: "-start", label: "Newest first", compare: reversed(byText<CurriculumRow>((row) => row.start_date)) },
    { key: "-subjects", label: "Most subjects", compare: reversed(byNumber<CurriculumRow>((row) => row.subjectCount)) },
  ],
};

// --- Syllabi and examinations (read here, managed on their own pages) ----------------

export const syllabusConfig: RegisterConfig<Syllabus> = {
  search: (row) => [row.title, row.subject_name, row.subject_code, row.grade_name, row.curriculum_name].join(" "),
  views: [
    { key: "all", label: "All" },
    { key: "active", label: "Active", match: (row) => row.is_active },
    { key: "inactive", label: "Inactive", match: (row) => !row.is_active },
  ],
  date: { label: "Starts", value: (row) => row.start_date },
  category: { label: "Class level", value: (row) => row.grade_name },
  sorts: [
    { key: "subject", label: "Subject (A–Z)", compare: byText<Syllabus>((row) => `${row.subject_name} ${row.grade_name}`) },
    { key: "level", label: "Class level", compare: byText<Syllabus>((row) => `${row.grade_name} ${row.subject_name}`) },
    { key: "-start", label: "Newest first", compare: reversed(byText<Syllabus>((row) => row.start_date)) },
  ],
};

export const examStatus = (exam: Pick<Exam, "status">) => {
  const dots: Record<string, string> = {
    draft: "bg-gray-400",
    scheduled: "bg-sky-500",
    in_progress: "bg-amber-500",
    completed: "bg-emerald-500",
    cancelled: "bg-red-500",
    postponed: "bg-amber-500",
  };
  return { label: labelFrom(EXAM_STATUSES, exam.status), dot: dots[exam.status] ?? "bg-gray-400" };
};

export const examConfig: RegisterConfig<Exam> = {
  search: (row) => [row.name, row.subject_name, row.exam_type_name, row.venue, ...(row.class_names ?? [])].join(" "),
  views: [
    { key: "all", label: "All" },
    { key: "draft", label: "Draft", match: (row) => row.status === "draft" },
    { key: "scheduled", label: "Scheduled", match: (row) => row.status === "scheduled" },
    { key: "in_progress", label: "In progress", match: (row) => row.status === "in_progress" },
    { key: "completed", label: "Completed", match: (row) => row.status === "completed" },
    { key: "off", label: "Cancelled or postponed", match: (row) => row.status === "cancelled" || row.status === "postponed" },
  ],
  date: { label: "Exam date", value: (row) => row.exam_date },
  category: { label: "Exam type", value: (row) => row.exam_type_name },
  sorts: [
    { key: "-date", label: "Newest first", compare: reversed(byText<Exam>((row) => `${row.exam_date} ${row.start_time}`)) },
    { key: "date", label: "Oldest first", compare: byText<Exam>((row) => `${row.exam_date} ${row.start_time}`) },
    { key: "name", label: "Name (A–Z)", compare: byText<Exam>((row) => row.name) },
  ],
};
